import {
    CheckCircleOutlined,
    RocketOutlined,
    StarOutlined
} from '@ant-design/icons';
import { Button, Col, Divider, message, Modal, Row, Skeleton, Space, Tooltip, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AiFillSafetyCertificate } from 'react-icons/ai';
import { FaChartLine, FaRegLightbulb } from 'react-icons/fa';
import { IoRocketSharp } from 'react-icons/io5';
import { Link, useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti';
import './homStyle.css';
import supabase from '../../../config/supabaseConfig';
import { useAuth } from '../../../Hooks/useAuth';
import { Plan } from '../../profile/lib/type';

const { Title, Paragraph, Text } = Typography;

interface PromotionModalProps {
    visible: boolean;
    onClose: () => void;
    userId?: string;
}

// The plans come from the database (the same ones as the "Devenir Pro" page and the
// admin), so prices and limits shown here are always the real ones. Loaded once,
// the first time the popup opens.
let plansPromise: Promise<Plan[]> | null = null;
const loadPlans = () => {
    if (!plansPromise) {
        plansPromise = Promise.resolve(
            supabase.from('plans').select('*').order('price', { ascending: true })
        ).then(({ data, error }) => {
            if (error) throw error;
            return (data as Plan[]) || [];
        }).catch((err) => {
            plansPromise = null; // try again next time
            throw err;
        });
    }
    return plansPromise;
};

// The plan highlighted as "POPULAIRE"
const RECOMMENDED_PLAN = 'Pro';

const formatPrice = (amount: number) => `${amount.toLocaleString('fr-FR')} CFA`;

const PromotionModal: React.FC<PromotionModalProps> = ({ visible, onClose, userId }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [showConfetti, setShowConfetti] = useState(false);
    const [plans, setPlans] = useState<Plan[] | null>(null);
    const [plansFailed, setPlansFailed] = useState(false);

    useEffect(() => {
        // "Réessayer" clears plansFailed, which runs this again
        if (!visible || plans || plansFailed) return;
        let cancelled = false;
        loadPlans()
            .then((data) => { if (!cancelled) setPlans(data); })
            .catch(() => { if (!cancelled) setPlansFailed(true); });
        return () => { cancelled = true; };
    }, [visible, plans, plansFailed]);

    // Function to navigate to the premium services tab
    const goToPremiumServices = () => {
        setShowConfetti(true);
        setTimeout(() => {
            // Close the modal after confetti shows
            setShowConfetti(false);
            onClose();

            // Get the current user ID from either props or auth context
            const userIdToUse = userId || user?.id;

            if (userIdToUse) {
                // Navigate to user profile with the premium services tab selected
                navigate(`/profile/${userIdToUse}?tab=3`);
            } else {
                message.info("Veuillez vous connecter pour accéder aux services premium");
                navigate('/auth/login');
            }
        }, 800);
    };

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            width={900}
            className="promotion-modal"
            centered
        >
            {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}

            <div className="bg-gradient-to-r from-primary-secondary via-accent-default to-accent-secondary -mt-6 -mx-6 px-6 py-10 rounded-t-lg relative overflow-hidden">
                {/* Background pattern */}
                <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>

                {/* Animated circles */}
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-primary-secondary/20 rounded-full blur-xl"></div>
                <div className="absolute -bottom-16 -right-16 w-60 h-60 bg-accent-secondary/20 rounded-full blur-xl"></div>

                <Row align="middle" justify="center" wrap={false} className="relative z-10">
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 100 }}
                        className="shrink-0"
                    >
                        <IoRocketSharp style={{ fontSize: 40, color: 'white', marginRight: 16 }} />
                    </motion.div>
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.5 }}
                    >
                        <Title level={2} style={{ color: 'white', margin: 0 }} className="!text-2xl md:!text-3xl">
                            Boostez vos ventes maintenant !
                        </Title>
                    </motion.div>
                </Row>

                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                >
                    <Paragraph className="text-center text-white/90 mt-3 max-w-lg mx-auto">
                        Plus d'annonces, plus de visibilité, plus de contacts : choisissez le plan qui vous convient.
                    </Paragraph>
                </motion.div>
            </div>

            <div className="px-0 sm:px-4 py-3">
                {/* Pricing Plans */}
                <div className="my-4">
                    {plansFailed ? (
                        <div className="text-center text-content-secondary py-6">
                            Impossible de charger les plans pour le moment.{' '}
                            <Button type="link" className="p-0" onClick={() => setPlansFailed(false)}>
                                Réessayer
                            </Button>
                        </div>
                    ) : !plans ? (
                        <Row gutter={[16, 16]} justify="center">
                            {[0, 1, 2].map((i) => (
                                <Col key={i} xs={24} sm={12} md={8}>
                                    <div className="rounded-xl border border-surface-tertiary p-5">
                                        <Skeleton active paragraph={{ rows: 4 }} />
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    ) : (
                        <Row gutter={[16, 16]} justify="center">
                            {plans.map((plan, index) => {
                                const recommended = plan.name === RECOMMENDED_PLAN;
                                const isFree = plan.price === 0;
                                const hasDiscount = !!plan.original_price && plan.original_price > plan.price;
                                return (
                                    // 4 plans: a 2x2 grid instead of 3 + 1 on its own
                                    <Col key={plan.id} xs={24} sm={12} md={plans.length === 4 ? 12 : 8}>
                                        <motion.div
                                            initial={{ y: 30, opacity: 0 }}
                                            animate={{ y: 0, opacity: 1 }}
                                            transition={{ delay: 0.3 + index * 0.1, duration: 0.4 }}
                                            className={`
                                                h-full flex flex-col rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all
                                                ${recommended ? 'border-2 border-primary-default' : 'border border-surface-tertiary'}
                                            `}
                                        >
                                            <div className={`${recommended ? 'bg-gradient-to-br from-primary-secondary to-accent-secondary' : isFree ? 'bg-surface-secondary' : 'bg-gradient-to-br from-accent-secondary/20 to-primary-secondary/20'} py-4 px-4 relative`}>
                                                {recommended && (
                                                    <div className="absolute top-0 right-0">
                                                        <div className="bg-primary-secondary text-light-default text-xs py-1 px-3 rounded-bl-lg font-bold">
                                                            POPULAIRE
                                                        </div>
                                                    </div>
                                                )}
                                                <h3 className={`text-lg font-bold mb-1 ${recommended ? 'text-light-default' : 'text-content-default'}`}>
                                                    {plan.name}
                                                </h3>
                                                <div className={`text-2xl font-bold ${recommended ? 'text-light-default' : 'text-content-default'}`}>
                                                    {isFree ? 'Gratuit' : formatPrice(plan.price)}
                                                    {!isFree && (
                                                        <span className={`text-sm font-normal ml-1 ${recommended ? 'text-light-default/80' : 'text-content-secondary'}`}>
                                                            {plan.interval}
                                                        </span>
                                                    )}
                                                </div>
                                                {hasDiscount && (
                                                    <div className={`text-sm line-through ${recommended ? 'text-light-default/70' : 'text-content-tertiary'}`}>
                                                        {formatPrice(plan.original_price as number)}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="p-5 bg-light-default flex-1 flex flex-col">
                                                <ul className="space-y-3 flex-1">
                                                    {plan.features.map((feature, i) => (
                                                        <motion.li
                                                            key={i}
                                                            initial={{ opacity: 0, x: -10 }}
                                                            animate={{
                                                                opacity: 1,
                                                                x: 0,
                                                                transition: { delay: 0.5 + i * 0.1 }
                                                            }}
                                                            className="flex items-start text-content-default"
                                                        >
                                                            <CheckCircleOutlined className="text-accent-default mr-2 mt-1" />
                                                            <span className="text-sm">{feature}</span>
                                                        </motion.li>
                                                    ))}
                                                </ul>

                                                <div className="mt-6">
                                                    <Button
                                                        type={recommended ? "primary" : "default"}
                                                        disabled={isFree}
                                                        block
                                                        size="large"
                                                        onClick={goToPremiumServices}
                                                        className={recommended ? "bg-gradient-to-r from-primary-secondary to-accent-secondary hover:from-primary-secondary hover:to-accent-secondary/90 shadow-md text-light-default" : "border border-primary-default text-primary-default hover:bg-primary-default hover:text-light-default"}
                                                    >
                                                        {isFree ? 'Inclus pour tous' : 'Choisir ce plan'}
                                                    </Button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    </Col>
                                );
                            })}
                        </Row>
                    )}
                </div>

                {/* Call to action */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.9 }}
                >
                    <Row justify="center" gutter={[16, 12]} className="mt-8">
                        <Col xs={24} sm="auto">
                            <Tooltip title="Voir nos différentes offres premium">
                                <Button
                                    type="primary"
                                    size="large"
                                    block
                                    className="bg-gradient-to-r from-primary-secondary to-accent-secondary border-0 h-12 px-8 hover:from-primary-secondary hover:to-accent-secondary/90 text-light-default"
                                    onClick={goToPremiumServices}
                                >
                                    <span className="flex items-center justify-center">
                                        <StarOutlined className="mr-2" />
                                        Devenir Premium
                                    </span>
                                </Button>
                            </Tooltip>
                        </Col>
                        <Col xs={24} sm="auto">
                            <Link to="/sell">
                                <Button
                                    size="large"
                                    block
                                    className="h-12 px-8 border border-primary-default text-primary-default hover:bg-primary-default hover:text-light-default transition"
                                    onClick={onClose}
                                >
                                    <span className="flex items-center justify-center">
                                        <RocketOutlined className="mr-2" />
                                        Vendre Maintenant
                                    </span>
                                </Button>
                            </Link>
                        </Col>
                    </Row>
                </motion.div>

                <Divider />

                {/* Feature highlights */}
                <Row gutter={[24, 24]} className="mb-8">
                    <Col xs={24} md={12}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                            whileHover={{ y: -5 }}
                            className="feature-card border rounded-lg p-5 h-full bg-light-default hover:border-primary-default"
                        >
                            <Space direction="vertical" size="middle" className="w-full">
                                <div className="flex items-center">
                                    <div className="p-2 rounded-full bg-primary-secondary/10 mr-3">
                                        <FaChartLine className="text-primary-secondary text-xl" />
                                    </div>
                                    <Text strong className="text-lg text-content-default">Visibilité accrue</Text>
                                </div>
                                <Paragraph className="text-content-secondary">
                                    Vos annonces premium apparaissent en tête des résultats de recherche et
                                    sont mises en avant sur notre page d'accueil.
                                </Paragraph>
                            </Space>
                        </motion.div>
                    </Col>

                    <Col xs={24} md={12}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.7 }}
                            whileHover={{ y: -5 }}
                            className="feature-card border rounded-lg p-5 h-full bg-light-default hover:border-primary-default"
                        >
                            <Space direction="vertical" size="middle" className="w-full">
                                <div className="flex items-center">
                                    <div className="p-2 rounded-full bg-accent-secondary/10 mr-3">
                                        <AiFillSafetyCertificate className="text-accent-secondary text-xl" />
                                    </div>
                                    <Text strong className="text-lg text-content-default">Badge premium</Text>
                                </div>
                                <Paragraph className="text-content-secondary">
                                    Un badge distingue vos annonces des autres et attire l'œil des acheteurs.
                                </Paragraph>
                            </Space>
                        </motion.div>
                    </Col>
                </Row>

                {/* Tip */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.8 }}
                    className="bg-surface-secondary p-6 rounded-xl mb-6 relative overflow-hidden"
                >
                    <div className="absolute top-0 left-0 w-20 h-20 bg-primary-secondary/10 rounded-full blur-xl"></div>

                    <Row align="middle" gutter={16}>
                        <Col xs={24} sm={4}>
                            <div className="flex justify-center mb-3 sm:mb-0">
                                <FaRegLightbulb className="text-primary-secondary text-4xl" />
                            </div>
                        </Col>
                        <Col xs={24} sm={20}>
                            <div>
                                <Text strong className="text-lg block mb-1 text-content-default">Le saviez-vous ?</Text>
                                <Paragraph className="text-content-secondary mb-0">
                                    Les annonces avec <span className="font-bold text-primary-default">plusieurs photos nettes</span> et
                                    un <span className="font-bold text-primary-default">prix clair</span> reçoivent plus de messages.
                                    Avec un plan premium, elles sont en plus mises en avant.
                                </Paragraph>
                            </div>
                        </Col>
                    </Row>
                </motion.div>

            </div>
        </Modal>
    );
};

export default PromotionModal;
